/**
 * MarkCompletePaperDialog — staff confirm packet forms complete from a paper scan.
 * Default: all applicable forms checked. Never sends email/DocuSeal.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel,
  List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, Typography,
} from '@mui/material';
import { getParentPacketStatus, setParentPacketPaperComplete, studentAPI } from '../../services/api';

export default function MarkCompletePaperDialog({
  open,
  onClose,
  studentId,
  documents = [],
  initialDocumentId = null,
  onDone,
  canUnmark = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState({});
  const [docId, setDocId] = useState(initialDocumentId || '');
  const [paperCompleted, setPaperCompleted] = useState(false);
  const [overall, setOverall] = useState('');

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        let docs = documents || [];
        if (!docs.length) {
          const res = await studentAPI.getDocuments(studentId);
          docs = Array.isArray(res.data) ? res.data : res.data?.results || [];
        }
        const preferred = initialDocumentId
          || (docs.find((d) => (d.file || '').toLowerCase().includes('.pdf')) || docs[0] || {}).id
          || '';
        if (!cancelled) setDocId(preferred || '');

        const statusRes = await getParentPacketStatus(studentId);
        const data = statusRes.data || {};
        const list = Array.isArray(data.forms) ? data.forms : [];
        const sel = {};
        list.forEach((f) => {
          // Pre-check all applicable; already-complete stay checked
          sel[f.form_id] = true;
        });
        if (!cancelled) {
          setForms(list);
          setSelected(sel);
          setPaperCompleted(!!data.paper_completed);
          setOverall(data.overall || '');
          if (data.paper_document_id) setDocId(data.paper_document_id);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load forms.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open, studentId, documents, initialDocumentId]);

  const toggle = (formId) => {
    setSelected((prev) => ({ ...prev, [formId]: !prev[formId] }));
  };

  const markComplete = async () => {
    const form_ids = forms.filter((f) => selected[f.form_id]).map((f) => f.form_id);
    if (!form_ids.length) {
      setError('Select at least one form.');
      return;
    }
    if (!docId) {
      setError('Select the uploaded paper packet PDF as evidence.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await setParentPacketPaperComplete(studentId, {
        complete: true,
        form_ids,
        document_id: docId,
      });
      setPaperCompleted(!!res.data?.paper_completed);
      setOverall(res.data?.overall || '');
      if (onDone) onDone(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to mark complete.');
    } finally {
      setSaving(false);
    }
  };

  const unmark = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await setParentPacketPaperComplete(studentId, { complete: false });
      setPaperCompleted(false);
      setOverall(res.data?.overall || '');
      if (onDone) onDone(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to unmark.');
    } finally {
      setSaving(false);
    }
  };

  const pdfDocs = (documents || []).filter((d) => {
    const name = (d.file || d.title || '').toLowerCase();
    return name.includes('.pdf');
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark complete (paper submission)</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confirm which forms are covered by the uploaded paper packet. This updates completion
          for the dashboard — it does not email parents or send to DocuSeal.
        </Typography>
        {paperCompleted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Already marked complete via paper{overall ? ` (status: ${overall})` : ''}.
          </Alert>
        )}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Evidence document (scan)</InputLabel>
          <Select
            label="Evidence document (scan)"
            value={docId || ''}
            onChange={(e) => setDocId(e.target.value)}
          >
            {(pdfDocs.length ? pdfDocs : documents).map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {(d.title || d.file || `Document ${d.id}`).toString().slice(0, 80)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : (
          <List dense>
            {forms.map((f) => (
              <ListItem key={f.form_id} disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={!!selected[f.form_id]}
                    onChange={() => toggle(f.form_id)}
                    disabled={f.completion_source === 'online' && f.is_complete}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={f.form_name || f.form_id}
                  secondary={
                    f.completion_source === 'paper'
                      ? 'Paper'
                      : f.completion_source === 'online'
                        ? 'Online'
                        : (f.is_complete ? 'Complete' : 'Incomplete')
                  }
                />
              </ListItem>
            ))}
            {!forms.length && (
              <Typography color="text.secondary">No applicable forms for this student.</Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Box>
          {canUnmark && paperCompleted && (
            <Button color="warning" onClick={unmark} disabled={saving}>
              Unmark paper complete
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={markComplete} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Mark complete'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
